/* eslint-disable complexity */
import { formatDate } from 'util/formatDate';
import { formatTime } from 'util/formatTime';
import { formatEventLocation } from 'util/event';
import { pxToEm } from 'util/pxToEm';
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { OpenInNewRounded, ChevronLeft, Check } from '@mui/icons-material';
import { FavoriteEventButton, ShareEventButton } from 'components/app';
import {
  AddToCalendarPlusIcon,
  CalendarEventIcon,
  FavoriteStarIcon,
  FavoriteStarFilledIcon,
  ClockIcon,
  LocationOn,
} from 'components/icons';
import LocalActivityFillIcon from 'components/icons/LocalActivityFillIcon';
import { colorWhite } from 'customColors';
import {
  OrganizationsCallout,
  EventHostStatus,
  OrganizationEventsModal,
  RichTextRenderer,
  SimilarEventsWidget,
  Button,
} from 'components/ui';
import ShareOutlinedIcon from 'components/icons/ShareOutlinedIcon';
import { Image, Link } from 'components/lib';
import { SchoolLogo } from 'components/ui/SchoolLogo';
import { useCanonicalUrl, useProfile } from 'hooks';
import { MainLayout } from 'layouts';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NotFoundPage from 'pages/404';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Event, useEventsApi, useReceiptsApi } from 'services/api';
import { Routes } from 'services/routes';
import {
  useSetStarredState,
  isStarredState,
} from 'state/starred-events/starred-events.state';
import { useRecoilValue } from 'recoil';
import useSWR, { useSWRConfig } from 'swr';
import { useSetOrganizationFollowedState } from 'state/followed-organizations/followed-organizations.state';
import { AddToCalendarButton } from 'components/app/AddToCalendarButton';
import features from 'config/features';
import { useSnackbar } from 'notistack';
import { EventCheckInSheet } from 'components/modals/event-check-in';
import { TimeSlotModal } from 'components/modals/TimeSlotModal';
export interface ViewEventPageProps {
  canonicalUrl?: string;
  path: string | null;
  event?: Event;
  eventId?: string;
}

const StyledFavoriteStarIcon = ({ selected }: { selected?: boolean }) => {
  if (selected)
    return <FavoriteStarFilledIcon fill={colorWhite} />;


  return <FavoriteStarIcon selected={selected} />;
};

export const ViewEventPage = ({
  canonicalUrl,
  path,
  ...props
}: ViewEventPageProps): ReactElement => {
  const router = useRouter();
  const params = router.query;
  const slug = params?.['event-slug'];
  const eventsApi = useEventsApi();
  const receiptsApi = useReceiptsApi();

  // Key used to fetch the event. May be a UUID (from SSR), a shortId
  // (forwarded when SSR fetched anonymously, e.g. members-only), or the URL
  // slug as a last resort.
  const fetchKey =
    props.event?.id ??
    props.eventId ??
    (typeof slug === 'string' ? slug : slug?.[0]);

  useCanonicalUrl(path);
  const rEvent = useSWR<Event>(fetchKey ? `/events/${fetchKey}` : null, {
    fallbackData: props.event,
    revalidateOnMount: true,
  });
  const event = rEvent.data;

  // The resolved event's real UUID. Use this (never the fetchKey, which can be
  // a shortId) for any call that needs the actual event identity — e.g.
  // viewEvent and reservation matching, which operate on UUIDs.
  const id = event?.id;
  useSetStarredState(event);
  useSetOrganizationFollowedState(event?.hostOrganizations);

  // Hoisted to the top level so the hook count stays stable across renders.
  // createActionButton is only invoked in the render path (after the loading /
  // NotFound early-returns), so calling useRecoilValue inside it would violate
  // the rules of hooks.
  const isStarred = useRecoilValue(isStarredState(id));

  const rReservations = useSWR<{
    count: number;
    reservations: {
      event: Event;
      receipt: {
        eventId: string;
        receiptId: string;
      };
      checkedIn: boolean;
    }[];
  }>('/events/reserved');
  // Stable identity so effects depending on `reservations` don't run every
  // render (the previous `?? []` created a new array each render).
  const reservations = useMemo(
    () => rReservations.data?.reservations ?? [],
    [rReservations.data],
  );
  const { mutate: globalMutate } = useSWRConfig();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [hasReservation, setHasReservation] = useState<boolean>();
  const [isCheckedIn, setIsCheckedIn] = useState<boolean>();
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketModalData, setTicketModalData] = useState<{
    receiptId: string;
    tickets: any[];
  } | null>(null);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<{
    id: string;
    name: string;
    logoUrl: string;
    schoolSlug: string;
  } | null>(null);

  const handleOrganizationClick = (
    organizationId: string,
    organizationName: string,
    organizationLogoUrl: string,
    schoolSlug?: string,
  ) => {
    setSelectedOrganization({
      id: organizationId,
      name: organizationName,
      logoUrl: organizationLogoUrl,
      schoolSlug: schoolSlug || event?.school?.slug || '',
    });
    setModalOpen(true);
  };

  const { profile, loading: profileLoading } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedRsvp, setHasAttemptedRsvp] = useState(false);
  // Ref-based guard so two clicks in the same tick can't both fire an RSVP
  // (state updates are async and wouldn't block the second click in time).
  const submitInFlightRef = useRef(false);

  // Revalidate every cache that reflects RSVP state: the reserved list (plain
  // and the "upcoming" variant used by the home rail), this event's detail
  // (which now carries `reserved`/`checkedIn`), and the school events browse
  // list + counts (whose cards show a reserved badge).
  const revalidateRsvpCaches = () => {
    const schoolSlug = event?.school?.slug;
    return globalMutate(
      (key: unknown) => {
        if (typeof key === 'string')
          return key.startsWith('/events/reserved') || key === `/events/${id}`;
        if (Array.isArray(key))
          return key[0] === 'events-count' || key[0] === schoolSlug;
        return false;
      },
      undefined,
      { revalidate: true },
    );
  };

  useEffect(() => {
    if (id) eventsApi.viewEvent(id).then();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    // Wait for auth to settle so we don't briefly treat an authenticated viewer
    // as anonymous.
    if (profileLoading) return;

    const reservation = reservations.find(r => r.event.id === id);
    // The event detail endpoint is authoritative for the current viewer and
    // survives refresh; the reservations list also carries the receiptId used
    // for check-in.
    const serverReserved = event?.reserved === true || !!reservation;

    // Within a session, don't let a not-yet-caught-up reservations refetch undo
    // an RSVP the user just made.
    if (hasAttemptedRsvp && !serverReserved) return;

    // For a logged-in viewer, only commit a value once we actually have their
    // reservation data — either the authenticated event payload (`reserved` is a
    // boolean) or the reserved list. Otherwise the button would flash "RSVP"
    // (from the anonymous SSR payload) before flipping to "Check In". Keeping
    // `hasReservation` undefined until then drives the skeleton loader.
    const hasViewerData =
      !profile ||
      typeof event?.reserved === 'boolean' ||
      rReservations.data !== undefined;

    if (!hasViewerData) return;

    setHasReservation(serverReserved);
    setIsCheckedIn(event?.checkedIn === true || !!reservation?.checkedIn);
  }, [
    reservations,
    id,
    hasAttemptedRsvp,
    event?.reserved,
    event?.checkedIn,
    profile,
    profileLoading,
    rReservations.data,
  ]);

  if (!fetchKey) return <NotFoundPage />;

  // No event yet (e.g. members-only, which SSR fetches anonymously) — wait for
  // the authenticated client fetch to settle before deciding it's a 404. Show
  // a loading state while that fetch is in flight so authorized viewers aren't
  // flashed a 404. Only render NotFoundPage once the fetch has resolved with
  // no event (truly missing or unauthorized).
  if (!event) {
    const hasSettled = rEvent.error || !rEvent.isValidating;
    if (!hasSettled) {
      return (
        <MainLayout>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
            }}
          >
            <CircularProgress />
          </Box>
        </MainLayout>
      );
    }

    return <NotFoundPage />;
  }

  const { school } = event;

  const liveStatuses: Event['status'][] = [
    'PUBLISHED',
    'UPDATED',
    'RESCHEDULED',
  ];
  const isLive = liveStatuses.includes(event.status);

  const allTicketTypes = [
    ...(event.ticketTypes ?? []).map(ticketType => ({
      ...ticketType,
      isGuestTicket: false,
    })),
    ...(event.guestTicketTypes ?? []).map(ticketType => ({
      ...ticketType,
      isGuestTicket: true,
    })),
  ];

  const handleAttendClick = async () => {
    if (submitInFlightRef.current || isSubmitting || hasReservation) return;

    const existingReservation = reservations.find(r => r.event.id === id);
    if (existingReservation) {
      setHasReservation(true);
      return;
    }

    if (
      event.ticketTypes?.length === 0 &&
      event.guestTicketTypes?.length === 0 &&
      event.timeSlots?.length === 0
    ) {
      await submitRsvp();
      return;
    }

    if (event.timeSlots && event.timeSlots.length > 0) {
      setShowTimeSlotModal(true);
      return;
    }

    router.push(Routes.reserveTickets(event.id));
  };

  const handleCheckIn = async (receiptId: string) => {
    const result = await receiptsApi.getReceipt(receiptId);
    if (!result.success) {
      enqueueSnackbar('Failed to load ticket', { variant: 'error' });
      return;
    }

    const { tickets } = result.value;
    setTicketModalData({
      receiptId,
      tickets,
    });
    setShowTicketModal(true);
  };

  const submitRsvp = async () => {
    if (submitInFlightRef.current || isSubmitting || hasReservation) return;

    const existingReservation = reservations.find(r => r.event.id === id);
    if (existingReservation) {
      setHasReservation(true);
      return;
    }

    submitInFlightRef.current = true;
    setHasAttemptedRsvp(true);
    setIsSubmitting(true);

    try {
      const result = await eventsApi.reserveTickets(event.id, {
        source:
          typeof window !== 'undefined' && window.ReactNativeWebView
            ? 'native'
            : 'web',
        tickets: [
          {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.primaryEmail || profile?.emails?.[0] || '',
          },
        ],
      });

      if (!result.success) {
        enqueueSnackbar(result.error.message, { variant: 'error' });
        // Reset so the button returns to "RSVP" instead of being stuck on
        // "Reserving..." — the reservation was not created.
        setHasAttemptedRsvp(false);
        return;
      }

      const { receiptId } = result.value;

      setHasReservation(true);

      setTicketModalData({
        receiptId,
        tickets: [
          {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.primaryEmail || profile?.emails?.[0] || '',
            ticketNumber: receiptId,
            typeId: event.ticketTypes?.[0]?.id,
          },
        ],
      });
      setShowTicketModal(true);

      // The RSVP is now persisted synchronously server-side, so revalidating
      // reflects the real state everywhere.
      await revalidateRsvpCaches();
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  const ticketInfoSection = (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box sx={{ width: 240, mx: 'auto' }}>
        <Typography
          sx={{
            fontSize: pxToEm(20),
            fontWeight: 500,
            mb: 8,
            mt: 32,
            textAlign: 'center',
          }}
        >
          Attending?
        </Typography>

        <Box mb={8}>
          {hasReservation === undefined ? (
            <Skeleton
              variant="rectangular"
              sx={{
                width: '100%',
                maxWidth: { md: 300 },
                mx: 'auto',
                height: 48,
                borderRadius: '8px',
              }}
            />
          ) : hasReservation ? (
              <Box sx={{ width: '100%', mx: 'auto' }}>
                {isCheckedIn ? (
                  <Box
                    onClick={() => {
                      const receiptId =
                        reservations.find(r => r.event.id === id)?.receipt
                          .receiptId || ticketModalData?.receiptId;
                      if (receiptId) handleCheckIn(receiptId);
                    }}
                    sx={{
                      width: '100%',
                      maxWidth: { md: 300 },
                      mx: 'auto',
                      height: 48,
                      borderRadius: '8px',
                      backgroundColor: '#E8EAEF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <Check sx={{ fontSize: 18, color: '#141F33' }} />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#141F33',
                      }}
                    >
                      Checked in
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="paper"
                    minHeight={48}
                    sx={{
                      minWidth: { md: 150 },
                      maxWidth: { md: 300 },
                      width: '100%',
                      borderRadius: 6,
                      border: theme =>
                        `1px solid ${theme.palette.primary.main}`,
                      background: theme => theme.palette.background.paper,
                    }}
                    style={{ width: '100%' }}
                    disabled={!isLive}
                    onClick={() => {
                      const receiptId =
                        reservations.find(r => r.event.id === id)?.receipt
                          .receiptId || ticketModalData?.receiptId;
                      if (receiptId) handleCheckIn(receiptId);
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={8}>
                      <LocalActivityFillIcon
                        sx={{
                          width: 18,
                          height: 14,
                          flexShrink: 0,
                          aspectRatio: '9 / 7',
                        }}
                      />
                      Check In
                    </Box>
                  </Button>
                )}
              </Box>
            ) : (
              <Button
                variant="vibrant"
                minHeight={48}
                sx={{
                  minWidth: { md: 150 },
                  maxWidth: { md: 300 },
                  width: '100%',
                }}
                style={{ width: '100%' }}
                disabled={!isLive || isSubmitting || hasAttemptedRsvp}
                onClick={() => {
                  void handleAttendClick();
                }}
              >
                {isSubmitting || hasAttemptedRsvp ? 'Reserving...' : 'RSVP'}
              </Button>
            )}
        </Box>
      </Box>
    </Box>
  );

  const eventOrganizersSection = (
    <Box mt={24}>
      <Typography
        style={{
          fontSize: pxToEm(14),
          fontWeight: 600,
        }}
      >
        Event Organizers
      </Typography>
      <OrganizationsCallout
        action="follow"
        slug={event.school?.slug}
        organizations={event.hostOrganizations}
        onOrganizationClick={handleOrganizationClick}
      />
    </Box>
  );

  const createActionButton = (text: string) => {
    let button = null;
    const isSaveButtonActive = text === 'Save' && isStarred;

    const label = (
      <Typography
        sx={{
          lineHeight: '28px',
          fontSize: { xs: pxToEm(12), sm: pxToEm(14) },
          display: 'inline-block',
          fontWeight: 400,
          color: isSaveButtonActive ? colorWhite : 'inherit',
        }}
      >
        {text}
      </Typography>
    );
    switch (text) {
      case 'Save':
        button = (
          <FavoriteEventButton
            eventId={event.id}
            icon={StyledFavoriteStarIcon}
            gap={8}
          >
            {label}
          </FavoriteEventButton>
        );
        break;
      case 'Share':
        button = (
          <ShareEventButton event={event} icon={ShareOutlinedIcon} gap={8}>
            {label}
          </ShareEventButton>
        );
        break;
      case 'Calendar':
        button = (
          <AddToCalendarButton
            event={event}
            icon={AddToCalendarPlusIcon}
            gap={8}
          >
            {label}
          </AddToCalendarButton>
        );
        break;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '1 1 0',
          minWidth: 0,
          minHeight: { xs: 44, sm: 48 },
          boxSizing: 'border-box',
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: { xs: '6px', sm: '8px' },
          px: { xs: 8, sm: 12 },
          py: { xs: 8, sm: 10 },
          transition: 'all 0.2s ease',
          backgroundColor: isSaveButtonActive
            ? theme => theme.palette.primary.main
            : 'transparent',
          borderColor: isSaveButtonActive
            ? theme => theme.palette.primary.main
            : undefined,
          '&:hover': {
            cursor: 'pointer',
            borderColor: theme => theme.palette.primary.main,
          },
        }}
      >
        <Box
          fontSize={{ xs: pxToEm(16), sm: pxToEm(16) }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          sx={{
            '& > *': {
              justifyContent: 'center',
              width: '100%',
            },
          }}
        >
          {button}
        </Box>
      </Box>
    );
  };

  const eventImageMobile = (
    <Box
      mx={{ xs: -20, md: 'unset' }}
      mt={{ xs: -32, md: 'unset' }}
      overflow="hidden"
      lineHeight={0}
      position="relative"
    >
      <Box sx={{ width: '100%', pt: '133.333%' }} />
      <Image
        src={event.flyerImageUrl}
        alt={`${event.school.name} - ${event.name}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 44,
          left: 12,
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          backgroundColor: colorWhite,
          borderRadius: '50%',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            backgroundColor: theme => theme.palette.grey[50],
          },
        }}
        onClick={() => router.back()}
      >
        <ChevronLeft
          sx={{ color: theme => theme.palette.text.primary, fontSize: 18 }}
        />
      </Box>
    </Box>
  );

  const eventImageDesktop = (
    <Box
      overflow="hidden"
      lineHeight={0}
      position="relative"
      sx={{
        width: { md: 450 },
        height: { md: 600 },
        aspectRatio: { md: '3 / 4' },
        mx: { md: 'auto' },
      }}
    >
      <Image
        src={event.flyerImageUrl}
        alt={`${event.school.name} - ${event.name}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top',
          borderRadius: 'inherit',
        }}
      />
    </Box>
  );

  return (
    <>
      <Head>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>
      <MainLayout hideMobileBottomNav>
        <MainLayout.Breadcrumb>
          <Link href={Routes.home()} style={{ fontWeight: 400 }}>
            Home
          </Link>
        </MainLayout.Breadcrumb>
        <MainLayout.Breadcrumb>
          <Link href={Routes.search(school.state)} style={{ fontWeight: 400 }}>
            {school.state}{' '}
          </Link>
        </MainLayout.Breadcrumb>
        <MainLayout.Breadcrumb>
          <Link href={Routes.explore(school.slug)} style={{ fontWeight: 400 }}>
            {school.name}
          </Link>
        </MainLayout.Breadcrumb>
        <MainLayout.Breadcrumb>{event.name}</MainLayout.Breadcrumb>

        <Box display={{ xs: 'block', md: 'none' }}>{eventImageMobile}</Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          maxWidth={1120}
          margin="auto"
        >
          <Box flex={1} py={20}>
            <Box
              pb={20}
              display="flex"
              alignItems="center"
              width="100%"
              gap={{ xs: '8px', sm: '12px' }}
            >
              {createActionButton('Save')}
              {createActionButton('Share')}
              {createActionButton('Calendar')}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              mt={16}
              mb={24}
              fontSize={{ xs: pxToEm(22), md: pxToEm(30) }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: 'inherit',
                  lineHeight: '1.5em',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  maxWidth: '100%',
                }}
              >
                {event.name}
              </Typography>
            </Box>
            <Box mb={24}>
              <Link
                href={Routes.explore(school.slug)}
                style={{ display: 'inline-block' }}
              >
                <SchoolLogo logoUrl={event.school.logoUrl} />
              </Link>
            </Box>
            <Box
              sx={{
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
              }}
              mb={24}
            />
            <Box>
              <Box display="flex" alignItems="center" mb={8}>
                <Box
                  fontSize={pxToEm(20)}
                  mr={10}
                  lineHeight={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={20}
                  height={20}
                >
                  <CalendarEventIcon />
                </Box>
                <Typography
                  style={{
                    fontSize: pxToEm(14),
                    lineHeight: '20px',
                  }}
                >
                  {formatDate(new Date(event.date), {
                    includeDate: true,
                    includeDay: true,
                  })}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={8}>
                <Box
                  fontSize={pxToEm(20)}
                  mr={10}
                  lineHeight={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={20}
                  height={20}
                >
                  <ClockIcon />
                </Box>
                <Typography
                  style={{
                    fontSize: pxToEm(14),
                    lineHeight: '20px',
                  }}
                >
                  {formatTime(event.startTime)}
                  {' - '}
                  {formatTime(event.endTime)}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              <Box
                fontSize={pxToEm(20)}
                mr={10}
                lineHeight={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={20}
                height={20}
              >
                <LocationOn />
              </Box>
              <Box display="flex" alignItems="center" lineHeight="28px">
                {allTicketTypes.length === 0 &&
                typeof event.setting === 'string' ? (
                  <Link href={event.setting} target="_blank" rel="noopener">
                    <Box display="flex" alignItems="center" whiteSpace="nowrap">
                      Click Here to Join Event
                      <OpenInNewRounded
                        style={{ marginLeft: 10, fontSize: pxToEm(16) }}
                      />
                    </Box>
                  </Link>
                ) : (
                  <Box fontSize={pxToEm(14)}>
                    {formatEventLocation(event.setting)}
                  </Box>
                )}
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
                mt={24}
                mb={24}
              />
              {!isLive && (
                <Box display="flex" mt={20}>
                  <EventHostStatus status={event.status} />
                </Box>
              )}

              <Box>
                <RichTextRenderer
                  html={event.description || ''}
                  style={{
                    fontSize: pxToEm(14),
                  }}
                />
              </Box>

              {eventOrganizersSection}
            </Box>
          </Box>
          <Box flex={1} padding={20}>
            <Box display={{ xs: 'none', md: 'block' }}>{eventImageDesktop}</Box>
            {features.eventTicketInformation && <>{ticketInfoSection}</>}
          </Box>
        </Box>
        <Box py={40}>
          <SimilarEventsWidget event={event} />
        </Box>
      </MainLayout>
      <Box
        display={{ xs: 'flex', md: 'none' }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={300}
        sx={{
          background: theme => theme.palette.background.paper,
          boxShadow: theme => theme.shadows[3],
        }}
        py={12}
        px={20}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography sx={{ fontSize: pxToEm(16), fontWeight: 500 }}>
            Attending?
          </Typography>
          {hasReservation === undefined ? (
            <Skeleton
              variant="rectangular"
              sx={{
                minHeight: 48,
                minWidth: { xs: 220, sm: 240, md: 260 },
                borderRadius: '8px',
              }}
            />
          ) : hasReservation ? (
            isCheckedIn ? (
              <Box
                onClick={() => {
                  const receiptId =
                    reservations.find(r => r.event.id === id)?.receipt
                      .receiptId || ticketModalData?.receiptId;
                  if (receiptId) handleCheckIn(receiptId);
                }}
                sx={{
                  minHeight: 48,
                  minWidth: { xs: 220, sm: 240, md: 260 },
                  px: '14px',
                  borderRadius: '8px',
                  backgroundColor: '#E8EAEF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                <Check sx={{ fontSize: 18, color: '#141F33' }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#141F33',
                  }}
                >
                  Checked in
                </Typography>
              </Box>
            ) : (
              <Button
                variant="paper"
                sx={{
                  minHeight: 48,
                  minWidth: { xs: 220, sm: 240, md: 260 },
                  px: { xs: 0, sm: 0, md: 24 },
                  py: 0,
                  lineHeight: 1.2,
                  borderRadius: 6,
                  border: theme => `1px solid ${theme.palette.primary.main}`,
                  background: theme => theme.palette.background.paper,
                }}
                disabled={!isLive}
                onClick={() => {
                  const receiptId =
                    reservations.find(r => r.event.id === id)?.receipt
                      .receiptId || ticketModalData?.receiptId;
                  if (receiptId) handleCheckIn(receiptId);
                }}
              >
                <Box display="flex" alignItems="center" gap={{ xs: 8, sm: 10 }}>
                  <LocalActivityFillIcon
                    sx={{
                      width: 18,
                      height: 14,
                      flexShrink: 0,
                      aspectRatio: '9 / 7',
                    }}
                  />
                  Check In
                </Box>
              </Button>
            )
          ) : (
            <Button
              variant="vibrant"
              sx={{
                minHeight: 48,
                minWidth: { xs: 220, sm: 240, md: 260 },
                px: { xs: 0, sm: 0, md: 24 },
                py: 0,
                lineHeight: 1.2,
              }}
              disabled={!isLive || isSubmitting || hasAttemptedRsvp}
              onClick={() => {
                void handleAttendClick();
              }}
            >
              {isSubmitting || hasAttemptedRsvp ? 'Reserving...' : 'RSVP'}
            </Button>
          )}
        </Box>
      </Box>

      {ticketModalData && (
        <EventCheckInSheet
          open={showTicketModal}
          onClose={() => {
            setShowTicketModal(false);
            void revalidateRsvpCaches();
          }}
          receiptId={ticketModalData.receiptId}
          tickets={ticketModalData.tickets.map(t => ({
            firstName: t.firstName,
            lastName: t.lastName,
            email: t.email,
            ticketNumber: t.ticketNumber,
            typeId: t.typeId,
            checkedInDate: t.checkInDate,
          }))}
          event={event}
        />
      )}

      <TimeSlotModal
        open={showTimeSlotModal}
        onClose={() => setShowTimeSlotModal(false)}
        event={event}
        onReservationComplete={() => {
          setHasAttemptedRsvp(true);
          setHasReservation(true);
          void revalidateRsvpCaches();
        }}
      />
      {selectedOrganization && (
        <OrganizationEventsModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedOrganization(null);
          }}
          organizationId={selectedOrganization.id}
          organizationName={selectedOrganization.name}
          organizationLogoUrl={selectedOrganization.logoUrl}
          schoolSlug={selectedOrganization.schoolSlug}
        />
      )}
    </>
  );
};
